import * as React from 'react';
import { getAuth } from 'firebase/auth';
import { addDoc } from 'firebase/firestore';

// @ld
// @custom
import Queue from 'utils/queue';
import getEventAt from 'utils/get-event-at';
import useSnackbar from 'hooks/use-snackbar';
import { getCollectionRef } from 'api/utils/refs';

import {
  IProfileDocumentWrite,
  TUploadFileSuccess,
  getProfileDocumentsPath
} from '@ld/utils';

// @local
import {
  TDocumentUpload,
  TDocumentUploadOptions,
  TDocumentUploadProcessing
} from './types';
import UploadSnackbar from './upload-snackbar';
import { DocumentUploadServiceContext } from './context';

const queue = new Queue<TDocumentUploadProcessing>();

type TAction = {
  type: 'add' | 'delete';
  payload: TDocumentUploadProcessing;
};

const reducer = (
  processingData: TDocumentUploadProcessing[],
  { type, payload }: TAction
) => {
  switch (type) {
    case 'add':
      return [...processingData, payload];
    case 'delete':
      return processingData.filter(
        ({ data }) => data.file.name !== payload.data.file.name
      );
    default:
      throw new Error();
  }
};

const DocumentUploadServiceProvider = ({
  profileId,
  children
}: {
  profileId: string;
  children: React.ReactNode;
}) => {
  const { showSnackbar } = useSnackbar();
  const [queueSize, setQueueSize] = React.useState<number>(0);
  const [uploadProcessingData, dispatch] = React.useReducer(reducer, []);

  const isLoading = uploadProcessingData.length > 0;

  const handleUploadFile = (data: TDocumentUpload, options?: TDocumentUploadOptions) => {
    queue.enqueue({ data, options });
    setQueueSize(queue.size);
  };

  const handleFileUploadComplete = async (
    processingData: TDocumentUploadProcessing,
    { fileName, downloadURL }: TUploadFileSuccess
  ) => {
    const user = getAuth().currentUser;
    if (user) {
      const collectionPath = getProfileDocumentsPath({
        profileId,
        entityId: processingData.data.entityId,
        categoryId: processingData.data.categoryId
      });
      const collectionRef = getCollectionRef(collectionPath);
      const eventAt = getEventAt({ uid: user.uid, name: user.displayName || '' });
      const documentData = processingData.data.data;
      const documentName = documentData.displayName || processingData.data.document.name;
      try {
        const newDocument: IProfileDocumentWrite = {
          ...documentData,
          fileName,
          url: downloadURL,
          created: eventAt.write,
          updated: eventAt.write
        };
        await addDoc(collectionRef, newDocument);
        dispatch({ type: 'delete', payload: processingData });
        showSnackbar({
          variant: 'success',
          message: `${documentName} uploaded successfully`
        });
      } catch (error) {
        processingData.options?.onError?.(error as Error);
        showSnackbar({
          variant: 'warning',
          message: `Failed to upload ${documentName}`
        });
      }
    }
  };

  React.useEffect(() => {
    if (uploadProcessingData.length < 3 && queueSize > 0) {
      const processingData = queue.dequeue();
      setQueueSize(queue.size);
      if (processingData) {
        dispatch({ type: 'add', payload: processingData });
      }
    }
  }, [uploadProcessingData.length, queueSize]);

  return (
    <DocumentUploadServiceContext.Provider
      value={{ isLoading, uploadProcessingData, handleUploadFile }}
    >
      {children}
      <UploadSnackbar
        open={isLoading}
        nextUploadProcessingData={queue.toArray()}
        uploadProcessingData={uploadProcessingData}
        onFileUploadComplete={handleFileUploadComplete}
      />
    </DocumentUploadServiceContext.Provider>
  );
};

export default DocumentUploadServiceProvider;
